import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Select,
  Input,
  VStack,
  HStack,
  Spacer,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import AnimalEpidemicChart from "./AnimalEpidemicChart";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const AnimalEpidemicReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    Province: "",
    DiseaseName: "",
    AnimalName: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [provinceOptions, setProvinceOptions] = useState([]);
  const [epidemicData, setEpidemicData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // ดึงข้อมูล Province List
  const fetchProvinceOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getAnimalEpidemicProvinceList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setProvinceOptions(response.ProvinceList || []);
      } else {
        console.error("Failed to fetch provinces:", response.error);
      }
    } catch (error) {
      console.error("Error fetching provinces:", error);
    }
  };

  // เรียก API getAnimalEpidemicSummary
  const fetchEpidemicReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getAnimalEpidemicSummary",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          Province: filterData.Province || "",
          DiseaseName: filterData.DiseaseName || "",
          AnimalName: filterData.AnimalName || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setEpidemicData(response.summary || []);
      } else {
        setEpidemicData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching epidemic report:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchProvinceOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่รายงาน</span>
        </HStack>
      ),
      selector: (row) => row.ReportDate,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จังหวัด</span>
        </HStack>
      ),
      selector: (row) => row.Province,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อโรค</span>
        </HStack>
      ),
      selector: (row) => row.DiseaseName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชนิดสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.AnimalName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสัตว์ทั้งหมด</span>
        </HStack>
      ),
      selector: (row) => row.Affected?.toLocaleString(),
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสัตว์ป่วย</span>
        </HStack>
      ),
      selector: (row) => row.Sick?.toLocaleString(),
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสัตว์ตาย</span>
        </HStack>
      ),
      selector: (row) => row.Died?.toLocaleString(),
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนสัตว์ป่วยรวมตาย</span>
        </HStack>
      ),
      selector: (row) => row.SickDied?.toLocaleString(),
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = epidemicData.map((item) => ({
      วันที่รายงาน: item.ReportDate,
      จังหวัด: item.Province,
      ชื่อโรค: item.DiseaseName,
      ชนิดสัตว์: item.AnimalName,
      จำนวนสัตว์ทั้งหมด: item.Affected,
      จำนวนสัตว์ป่วย: item.Sick,
      จำนวนสัตว์ตาย: item.Died,
      จำนวนสัตว์ป่วยรวมตาย: item.SickDied,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "EpidemicReport");
    XLSX.writeFile(wb, "AnimalEpidemicReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานการเกิดโรคระบาดในสัตว์", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "วันที่รายงาน",
          "จังหวัด",
          "ชื่อโรค",
          "ชนิดสัตว์",
          "จำนวนสัตว์ทั้งหมด",
          "จำนวนสัตว์ป่วย",
          "จำนวนสัตว์ตาย",
          "จำนวนสัตว์ป่วยรวมตาย",
        ],
      ],
      body: epidemicData.map((item) => [
        item.ReportDate,
        item.Province,
        item.DiseaseName,
        item.AnimalName,
        item.Affected,
        item.Sick,
        item.Died,
        item.SickDied,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 10,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 30 },
        2: { cellWidth: 40 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 30 },
        6: { cellWidth: 30 },
        7: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("AnimalEpidemicReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Animal Epidemic Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Animal Epidemic Report</h2>
          <table>
            <thead>
              <tr>
                <th>วันที่รายงาน</th>
                <th>จังหวัด</th>
                <th>ชื่อโรค</th>
                <th>ชนิดสัตว์</th>
                <th>จำนวนสัตว์ทั้งหมด</th>
                <th>จำนวนสัตว์ป่วย</th>
                <th>จำนวนสัตว์ตาย</th>
                <th>จำนวนสัตว์ป่วยรวมตาย</th>
              </tr>
            </thead>
            <tbody>
              ${epidemicData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.ReportDate}</td>
                      <td>${item.Province}</td>
                      <td>${item.DiseaseName}</td>
                      <td>${item.AnimalName}</td>
                      <td>${item.Affected}</td>
                      <td>${item.Sick}</td>
                      <td>${item.Died}</td>
                      <td>${item.SickDied}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={35} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการเกิดโรคระบาดในสัตว์" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลโรคระบาด"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5} mt={10}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่พบโรค (เริ่มต้น)
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่พบโรค (สิ้นสุด)
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                จังหวัด
              </FormLabel>
              <Select
                name="Province"
                value={filterData.Province}
                onChange={handleInputChange}
                placeholder="เลือกจังหวัด (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {provinceOptions.map((province) => (
                  <option key={province.Province} value={province.Province}>
                    {province.Province}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ชื่อโรค
              </FormLabel>
              <Input
                type="text"
                name="DiseaseName"
                value={filterData.DiseaseName}
                onChange={handleInputChange}
                placeholder="ระบุชื่อโรค (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ชนิดสัตว์
              </FormLabel>
              <Input
                type="text"
                name="AnimalName"
                value={filterData.AnimalName}
                onChange={handleInputChange}
                placeholder="ระบุชนิดสัตว์ (บางส่วน)"
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    Province: "",
                    DiseaseName: "",
                    AnimalName: "",
                  })
                }
              />
              <Search onClick={fetchEpidemicReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {epidemicData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการโรคระบาดในสัตว์"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={epidemicData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {epidemicData.length > 0 && <AnimalEpidemicChart data={epidemicData} />}
    </Box>
  );
};

export default AnimalEpidemicReport;
